import { FlagCheckeredIcon } from "@phosphor-icons/react/FlagCheckered";
import { MapPinIcon } from "@phosphor-icons/react/MapPin";
import { PackageIcon } from "@phosphor-icons/react/Package";
import { Divider } from "@freightsimple/ui-core/ComponentLayout/Divider";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { getSingularOrPluralFormForHandlingUnitType } from "@/Data/getSingularOrPluralFormForHandlingUnitType";
import { groupBy } from "@/Helpers/groupBy";
import { sumOf } from "@/Helpers/sumOf";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { ReactNode } from "react";

interface UploadPhotosShipmentDescriptionProps {
  pickupCompanyName: string;
  pickupCity: string;
  pickupState: string;
  deliveryCompanyName: string;
  deliveryCity: string;
  deliveryState: string;
  lineItems: LineItem[];
}

function countHandlingUnits(lineItems: LineItem[]): number {
  return sumOf(lineItems, (li) => li.numberHandlingUnits);
}

function describeHandlingUnits(lineItems: LineItem[]): string {
  const grouped = groupBy(lineItems, (li) => li.handlingUnitType);
  const firstLineItem = lineItems[0];
  if (grouped.length > 1 || firstLineItem === undefined) {
    return "Handling Units";
  } else {
    return getSingularOrPluralFormForHandlingUnitType(
      firstLineItem.handlingUnitType,
      countHandlingUnits(lineItems),
    );
  }
}

function describeLineItems(lineItems: LineItem[]): string {
  const count = countHandlingUnits(lineItems);
  const description = describeHandlingUnits(lineItems);
  const totalWeight = sumOf(
    lineItems,
    (li) => li.numberHandlingUnits * li.weightPerHandlingUnit,
  );
  return `${count} x ${description} (Total ${totalWeight}lb)`;
}

function describeLineItemDescriptions(lineItems: LineItem[]): string {
  const grouped = groupBy(lineItems, (li) => li.description);
  const firstLineItem = lineItems[0];

  if (firstLineItem === undefined) {
    return "";
  }
  if (grouped.length > 1) {
    return `${firstLineItem.description}...`;
  } else {
    return firstLineItem.description;
  }
}

// One stop on the route: its icon, then a small label above the business name
// and its city. Each stop gets the card's full width so long business names
// wrap to a couple of lines instead of a tall, narrow column. A line runs down
// from the first stop's icon to the next stop, so the two read as a route.
function RouteStop_({
  icon,
  label,
  companyName,
  place,
  lineToNextStop,
}: {
  icon: ReactNode;
  label: string;
  companyName: string;
  place: string;
  lineToNextStop: boolean;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center pt-0.5 text-brand">
        {icon}
        {lineToNextStop && (
          <span
            aria-hidden
            className="mt-1 w-0.5 flex-1 rounded-full bg-border"
          />
        )}
      </div>
      <div className={lineToNextStop ? "min-w-0 pb-4" : "min-w-0"}>
        <Stack gap={0.5}>
          <T size="sm" color="muted">
            {label}
          </T>
          <T weight="semibold">{companyName}</T>
          <T size="sm" color="secondary">
            {place}
          </T>
        </Stack>
      </div>
    </div>
  );
}

export function UploadPhotosShipmentDescription(
  props: UploadPhotosShipmentDescriptionProps,
) {
  return (
    <Stack gap={3} data-testid="upload-photos-shipment-description">
      <Heading level={2} size="base">
        Your shipment
      </Heading>
      <div className="flex flex-col gap-4 rounded-xl bg-muted p-4">
        <div>
          <RouteStop_
            icon={<MapPinIcon weight="fill" size={18} aria-hidden />}
            label="From"
            companyName={props.pickupCompanyName}
            place={`${props.pickupCity}, ${props.pickupState}`}
            lineToNextStop
          />
          <RouteStop_
            icon={<FlagCheckeredIcon weight="fill" size={18} aria-hidden />}
            label="To"
            companyName={props.deliveryCompanyName}
            place={`${props.deliveryCity}, ${props.deliveryState}`}
            lineToNextStop={false}
          />
        </div>
        <Divider />
        <div className="flex items-start gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-subtle text-brand">
            <PackageIcon weight="fill" size={20} aria-hidden />
          </span>
          <Stack gap={0.5}>
            <T weight="semibold">{describeLineItems(props.lineItems)}</T>
            <T size="sm" color="secondary">
              {describeLineItemDescriptions(props.lineItems)}
            </T>
          </Stack>
        </div>
      </div>
    </Stack>
  );
}
